/**
 * Voucher types as CONFIGURATION, not as six separate screens.
 *
 * The original project had CashReceipt.jsx (550 lines), CashReceiptForm.jsx
 * (1,036 lines) and two journal screens -- all hardcoded to a single type.
 * One config-driven form replaces all of them, so a fix to the keyboard
 * flow fixes every voucher type at once.
 */
import { P } from '../api/types'
import type { AccountRole, DrCr, VoucherKind } from '../api/types'

export interface SeedLine {
  dr_cr: DrCr
  /** Prefill by semantic role so no account code is hardcoded. */
  role?: AccountRole
  /** Restrict the picker to these account groups, or leave open. */
  groups?: string[]
  label?: string
}

export interface VoucherTypeConfig {
  kind: VoucherKind
  slug: string
  labelKey: string
  combo: string
  permission: string
  /** Rows the form opens with. */
  seed: SeedLine[]
  /** Which side the user usually types the amount into first. */
  focusRow: number
}

const G = {
  cashBank: ['CASH', 'BANK'],
  parties: ['DEBTORS', 'CREDIT'],
  debtors: ['DEBTORS'],
  creditors: ['CREDIT'],
}

export const VOUCHER_TYPES: VoucherTypeConfig[] = [
  {
    kind: 'CONTRA', slug: 'contra', labelKey: 'voucher.contra',
    combo: 'alt+4', permission: P.postContra, focusRow: 0,
    // Cash <-> bank only. Tally restricts contra the same way.
    seed: [
      { dr_cr: 'D', groups: G.cashBank },
      { dr_cr: 'C', groups: G.cashBank },
    ],
  },
  {
    kind: 'PAYMENT', slug: 'payment', labelKey: 'voucher.payment',
    combo: 'alt+5', permission: P.postPayment, focusRow: 0,
    seed: [
      { dr_cr: 'D' },                          // supplier or expense
      { dr_cr: 'C', groups: G.cashBank },      // paid from
    ],
  },
  {
    kind: 'RECEIPT', slug: 'receipt', labelKey: 'voucher.receipt',
    combo: 'alt+6', permission: P.postReceipt, focusRow: 1,
    seed: [
      { dr_cr: 'D', groups: G.cashBank },      // received into
      { dr_cr: 'C' },                          // customer or income
    ],
  },
  {
    kind: 'JOURNAL', slug: 'journal', labelKey: 'voucher.journal',
    combo: 'alt+7', permission: P.postJournal, focusRow: 0,
    seed: [{ dr_cr: 'D' }, { dr_cr: 'C' }],
  },
  {
    kind: 'SALES', slug: 'sales', labelKey: 'voucher.sales',
    combo: 'alt+8', permission: P.postSales, focusRow: 0,
    seed: [
      { dr_cr: 'D', groups: G.debtors },
      { dr_cr: 'C', role: 'SALES' },
      { dr_cr: 'C', role: 'VAT_OUTPUT' },
    ],
  },
  {
    kind: 'PURCHASE', slug: 'purchase', labelKey: 'voucher.purchase',
    combo: 'alt+9', permission: P.postPurchase, focusRow: 0,
    seed: [
      { dr_cr: 'D', role: 'PURCHASES' },
      { dr_cr: 'D', role: 'VAT_INPUT' },
      { dr_cr: 'C', groups: G.creditors },
    ],
  },
]

export function configForSlug(slug: string): VoucherTypeConfig | undefined {
  return VOUCHER_TYPES.find((v) => v.slug === slug)
}

/** VAT rate for KSA. Used to auto-fill the tax line during entry. */
export const VAT_RATE = '0.15'
